/**
 * The one renderer for feed creatives.
 *
 * Step 4's previews, step 5's gallery thumbnails and the full-size files that
 * are uploaded all come out of renderCreative(). Previewing with one engine and
 * producing with another is how "it looked fine in preview" happens; with one
 * function the only difference between a thumbnail and the real file is the
 * pixel size it is drawn at.
 *
 * LAYOUT MODEL
 * Positions are stored as fractions of the canvas (x, y, w of the width and
 * height) and font sizes as a fraction of the shorter side, so one layout
 * draws identically at 300px on screen and 1080px in the file. Styling that
 * should match across sizes (font, colour, alignment, lines allowed) lives on
 * the slot definition; position and size live per format.
 *
 *   layout = {
 *     slots:  [{ key, label, max_chars, maxLines, fontFamily, fontWeight,
 *                fill, align, bg }],
 *     sizes:  { '1:1': { slots: { headline: { x, y, w, f } },
 *                        logo: { x, y, w } | null } },
 *     sample: { headline: 'Autumn sale in Leeds', ... },
 *     logoId: 'brand-asset-id' | null,
 *   }
 *
 * TOO-LONG TEXT
 * Each slot allows a number of lines. Text that wraps to more is shrunk a step
 * at a time down to MIN_SHRINK of its set size; if it still does not fit it is
 * drawn at that size and reported as overflowing, so the person decides
 * rather than the renderer quietly cutting words.
 */

export const SIZES = [
  { key: '1:1',    label: 'Square',    sub: 'Feed',         w: 1080, h: 1080, tag: '1x1' },
  { key: '4:5',    label: 'Portrait',  sub: 'Feed',         w: 1080, h: 1350, tag: '4x5' },
  { key: '9:16',   label: 'Story',     sub: 'Story / Reel', w: 1080, h: 1920, tag: '9x16' },
  { key: '1.91:1', label: 'Landscape', sub: 'Feed',         w: 1200, h: 628,  tag: '191x1' },
];
export const SIZE = Object.fromEntries(SIZES.map((s) => [s.key, s]));

export const MIN_SHRINK = 0.6;
const LINE_HEIGHT = 1.12;

/** Pill padding around a slot with a background, as a fraction of its font size. */
const PILL_PAD_X = 0.75;
const PILL_PAD_Y = 0.4;

export function slugify(text) {
  return (text || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 60);
}

/** Mirrors studio_feed.asset_name on the server; the server's is the one stored. */
export function assetName(brand, campaign, variant, sizeKey) {
  return [slugify(brand), slugify(campaign), slugify(variant), SIZE[sizeKey].tag]
    .filter(Boolean).join('_');
}

/** Starting positions for a format that has none yet: copy the nearest one laid out. */
export function deriveSizeLayout(layout, sizeKey) {
  const existing = layout.sizes[sizeKey];
  if (existing) return existing;
  const from = Object.values(layout.sizes)[0];
  if (from) return JSON.parse(JSON.stringify(from));
  return { slots: {}, logo: null };
}

/** The pill drawn behind a slot that has a background colour. */
export function pillGeometry(tb, align) {
  const textW = tb.calcTextWidth();
  const h = tb.height;
  const fs = tb.fontSize;
  const padX = fs * PILL_PAD_X;
  const padY = fs * PILL_PAD_Y;
  let left = tb.left;
  if (align === 'center') left = tb.left + (tb.width - textW) / 2;
  if (align === 'right') left = tb.left + tb.width - textW;
  return {
    left: left - padX, top: tb.top - padY,
    width: textW + padX * 2, height: h + padY * 2,
    rx: (h + padY * 2) / 2, ry: (h + padY * 2) / 2,
  };
}

/** Load an image element once; every render reuses it without re-decoding. */
export function loadImageElement(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('The image could not be read.'));
    img.src = src;
  });
}

/** Make sure every family a layout uses is loaded before anything is drawn. */
export async function ensureFonts(layout) {
  const wanted = new Set(
    (layout.slots || []).map((s) => `${s.fontWeight || 400} 40px "${s.fontFamily}"`)
  );
  await Promise.all([...wanted].map((f) => document.fonts.load(f).catch(() => null)));
  await document.fonts.ready;
}

/** Place the base picture to cover the canvas, centred. */
export function coverImage(fabric, imgEl, W, H) {
  const img = new fabric.FabricImage(imgEl, { selectable: false, evented: false });
  const cover = Math.max(W / imgEl.naturalWidth, H / imgEl.naturalHeight);
  img.set({
    originX: 'center', originY: 'center', left: W / 2, top: H / 2,
    scaleX: cover, scaleY: cover,
  });
  img.bsRole = 'artwork';
  return img;
}

/**
 * Build one slot's textbox at the given canvas size and fit its text.
 * Returns { tb, overflow } where overflow means it did not fit even shrunk.
 */
export function buildSlotText(fabric, slotDef, pos, text, W, H) {
  const short = Math.min(W, H);
  const setSize = Math.max(4, pos.f * short);
  const tb = new fabric.Textbox(text || '', {
    left: pos.x * W, top: pos.y * H, width: Math.max(10, pos.w * W),
    fontSize: setSize,
    fontFamily: slotDef.fontFamily || 'Sora',
    fontWeight: slotDef.fontWeight || 600,
    fill: slotDef.fill || '#0f2e24',
    textAlign: slotDef.align || 'left',
    lineHeight: LINE_HEIGHT,
    splitByGrapheme: false,
  });
  tb.bsSlot = slotDef.key;

  const maxLines = Math.max(1, Number(slotDef.maxLines) || 2);
  const floor = setSize * MIN_SHRINK;
  let overflow = false;
  tb.initDimensions();
  while (tb.textLines.length > maxLines && tb.fontSize > floor) {
    tb.set('fontSize', Math.max(floor, tb.fontSize * 0.94));
    tb.initDimensions();
  }
  if (tb.textLines.length > maxLines) overflow = true;
  return { tb, overflow };
}

/**
 * Draw one creative.
 *
 *   base     an HTMLImageElement (loaded once by the caller)
 *   logoEl   an HTMLImageElement or null
 *   values   { slotKey: text }
 *   outW     pixel width to draw at; height follows the format's ratio
 *
 * Returns { canvas, overflow: [slotKeys] }. The caller disposes the canvas.
 */
export function renderCreative({ fabric, base, logoEl, layout, sizeKey, values, outW }) {
  const spec = SIZE[sizeKey];
  const W = Math.round(outW || spec.w);
  const H = Math.round(W * (spec.h / spec.w));
  const el = document.createElement('canvas');
  const canvas = new fabric.StaticCanvas(el, {
    width: W, height: H, enableRetinaScaling: false, backgroundColor: '#ffffff',
  });

  if (base) canvas.add(coverImage(fabric, base, W, H));

  const sizeLayout = deriveSizeLayout(layout, sizeKey);
  const overflow = [];
  (layout.slots || []).forEach((slotDef) => {
    const pos = sizeLayout.slots?.[slotDef.key];
    if (!pos) return;
    const { tb, overflow: over } = buildSlotText(fabric, slotDef, pos, values?.[slotDef.key] ?? '', W, H);
    if (over) overflow.push(slotDef.key);
    if (slotDef.bg && (tb.text || '').trim()) {
      canvas.add(new fabric.Rect({ ...pillGeometry(tb, slotDef.align), fill: slotDef.bg }));
    }
    canvas.add(tb);
  });

  if (logoEl && sizeLayout.logo) {
    const lg = sizeLayout.logo;
    const img = new fabric.FabricImage(logoEl);
    img.scaleToWidth(lg.w * W);
    img.set({ left: lg.x * W, top: lg.y * H });
    canvas.add(img);
  }

  canvas.renderAll();
  return { canvas, overflow, width: W, height: H };
}

/** A rendered canvas as a PNG blob at exactly its own pixel size. */
export function canvasToPngBlob(canvas) {
  return new Promise((resolve, reject) => {
    canvas.getElement().toBlob(
      (b) => (b ? resolve(b) : reject(new Error('The creative could not be exported.'))),
      'image/png'
    );
  });
}

/** Re-encode any image as PNG, keeping its pixel size. Used so the base the
 *  model receives is always the format the server labels it as. */
export async function toPngDataUrl(dataUrl) {
  if (dataUrl.startsWith('data:image/png')) return dataUrl;
  const img = await loadImageElement(dataUrl);
  const c = document.createElement('canvas');
  c.width = img.naturalWidth;
  c.height = img.naturalHeight;
  c.getContext('2d').drawImage(img, 0, 0);
  return c.toDataURL('image/png');
}

/**
 * How bright the base image is behind a slot, 0 (black) to 1 (white).
 *
 * Draws the base as it would be cropped for this size into a small canvas and
 * averages relative luminance over the slot's box (its width, and the height
 * of the lines it allows). Used to pick a readable default colour when a slot
 * is added: dark pine on a dark photo was unreadable in the 30 Oct run-through.
 * The person can still change it; this only sets where it starts.
 */
export function regionLuminance(imgEl, sizeKey, pos, lines = 1) {
  if (!imgEl || !pos) return 1;
  const spec = SIZE[sizeKey];
  const W = 120;
  const H = Math.round(W * (spec.h / spec.w));
  const c = document.createElement('canvas');
  c.width = W;
  c.height = H;
  const ctx = c.getContext('2d', { willReadFrequently: true });
  const cover = Math.max(W / imgEl.naturalWidth, H / imgEl.naturalHeight);
  const dw = imgEl.naturalWidth * cover;
  const dh = imgEl.naturalHeight * cover;
  ctx.drawImage(imgEl, (W - dw) / 2, (H - dh) / 2, dw, dh);
  const x = Math.max(0, Math.min(W - 1, Math.floor(pos.x * W)));
  const y = Math.max(0, Math.min(H - 1, Math.floor(pos.y * H)));
  const w = Math.max(1, Math.min(W - x, Math.ceil(pos.w * W)));
  const h = Math.max(1, Math.min(H - y, Math.ceil(pos.f * Math.min(W, H) * LINE_HEIGHT * lines)));
  const d = ctx.getImageData(x, y, w, h).data;
  let sum = 0;
  for (let i = 0; i < d.length; i += 4) {
    sum += (0.2126 * d[i] + 0.7152 * d[i + 1] + 0.0722 * d[i + 2]) / 255;
  }
  return sum / (d.length / 4);
}
